import { useEffect, useCallback } from 'react';

const STORAGE_KEY = 'app-last-activity';
const DEFAULT_TIMEOUT_MS = 12 * 60 * 60 * 1000; // 12 hours
// 12 * 60 * 60 * 1000; // 12 hours
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'mousemove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
];

const getLastActivity = (): number => {
  const value = localStorage.getItem(STORAGE_KEY);
  return value ? Number(value) : 0;
};

const setLastActivity = (): void => {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
};

export default function useAutoLogoutOnInactivity(
  isAuthenticated: boolean,
  onLogout: () => void,
  timeoutMs = DEFAULT_TIMEOUT_MS
): void {
  const checkInactivity = useCallback(() => {
    const lastActivity = getLastActivity();
    if (!lastActivity || Date.now() - lastActivity < timeoutMs) {
      return;
    }

    onLogout();
  }, [onLogout, timeoutMs]);

  const handleActivity = useCallback(() => {
    if (isAuthenticated) {
      setLastActivity();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    setLastActivity();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkInactivity();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    window.addEventListener('focus', handleActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(checkInactivity, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      window.removeEventListener('focus', handleActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [checkInactivity, handleActivity, isAuthenticated]);
}
